import { useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  createSubscriptionSchema,
  type BillingCycleSchema,
  type CreateSubscriptionDto,
  type CreateSubscriptionInput,
  type PaymentMethodSchema,
  type SaleOriginSchema,
  parseLocalDate,
  todayLocal,
} from '@control-center/shared';

import { NewClientDialog } from '../../../components/clients/NewClientDialog';
import { extractErrorMessage } from '../../../lib/errors';
import type { ClientEntity } from '../../../lib/clients-api';
import type { PlanListItem } from '../../../lib/plans-api';
import { useCreateSubscription } from '../../../hooks/use-subscriptions';
import { Step1Client } from './Step1Client';
import { Step2Plan } from './Step2Plan';
import { Step3Details } from './Step3Details';
import { Step4Confirm } from './Step4Confirm';

type Step = 1 | 2 | 3 | 4;

export interface WizardData {
  client: ClientEntity | null;
  plan: PlanListItem | null;
  start_date: string;
  billing_cycle: BillingCycleSchema;
  payment_method: PaymentMethodSchema | null;
  sold_by_user_id: string | null;
  sale_origin: SaleOriginSchema | null;
  observations: string;
  auto_renew: boolean;
}

interface WizardState {
  step: Step;
  data: WizardData;
}

type WizardAction =
  | { type: 'SET_STEP'; step: Step }
  | { type: 'PATCH'; patch: Partial<WizardData> }
  | { type: 'RESET' };

const EMPTY_DATA: WizardData = {
  client: null,
  plan: null,
  start_date: todayLocal(),
  billing_cycle: 'mensal',
  payment_method: null,
  sold_by_user_id: null,
  sale_origin: null,
  observations: '',
  auto_renew: true,
};

function reducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'PATCH':
      return { ...state, data: { ...state.data, ...action.patch } };
    case 'RESET':
      return { step: 1, data: EMPTY_DATA };
    default:
      return state;
  }
}

const STEP_LABELS: { n: Step; label: string }[] = [
  { n: 1, label: 'Cliente' },
  { n: 2, label: 'Plano' },
  { n: 3, label: 'Detalhes' },
  { n: 4, label: 'Confirma' },
];

export function SubscriptionWizardPage(): JSX.Element {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, { step: 1, data: EMPTY_DATA });
  const [newClientOpen, setNewClientOpen] = useState(false);
  const create = useCreateSubscription();

  function cancel(): void {
    if (window.confirm('Cancelar criação da assinatura? Dados serão perdidos.')) {
      navigate('/subscriptions');
    }
  }

  async function submit(): Promise<void> {
    if (!state.data.client || !state.data.plan) {
      toast.error('Cliente e plano são obrigatórios.');
      return;
    }
    try {
      const input: CreateSubscriptionInput = {
        client_id: state.data.client.id,
        plan_id: state.data.plan.id,
        start_date: parseLocalDate(state.data.start_date),
        billing_cycle: state.data.billing_cycle,
        payment_method: state.data.payment_method,
        auto_renew: state.data.auto_renew,
        observations: state.data.observations.trim() === '' ? null : state.data.observations,
        sold_by_user_id: state.data.sold_by_user_id,
        sale_origin: state.data.sale_origin,
      };
      const parsed: CreateSubscriptionDto = createSubscriptionSchema.parse(input);
      const created = await create.mutateAsync(parsed);
      toast.success('Assinatura criada.');
      navigate(`/subscriptions/${created.id}`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos criar a assinatura.'));
    }
  }

  return (
    <div className="space-y-5">
      <header className="space-y-1">
        <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Nova assinatura</h1>
        <p className="text-[13px] text-muted">Wizard guiado em 4 passos.</p>
      </header>

      <ol className="flex flex-wrap items-center gap-2">
        {STEP_LABELS.map(({ n, label }, idx) => {
          const isCurrent = state.step === n;
          const isDone = state.step > n;
          return (
            <li key={n} className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold transition-colors ${
                  isDone
                    ? 'bg-good text-page'
                    : isCurrent
                      ? 'bg-ink text-page'
                      : 'bg-panel-3 text-muted-2'
                }`}
              >
                {n}
              </span>
              <span className={`text-[13px] ${isCurrent ? 'font-medium text-ink' : 'text-muted'}`}>
                {label}
              </span>
              {idx < STEP_LABELS.length - 1 && <span className="mx-2 h-px w-12 bg-line" />}
            </li>
          );
        })}
      </ol>

      <div className="rounded-[14px] border border-line bg-panel p-6">
        {state.step === 1 && (
          <Step1Client
            value={state.data.client}
            onChange={(client) => dispatch({ type: 'PATCH', patch: { client } })}
            onCreateNew={() => setNewClientOpen(true)}
            onNext={() => dispatch({ type: 'SET_STEP', step: 2 })}
            onCancel={cancel}
          />
        )}
        {state.step === 2 && (
          <Step2Plan
            value={state.data.plan}
            onChange={(plan) => dispatch({ type: 'PATCH', patch: { plan } })}
            onBack={() => dispatch({ type: 'SET_STEP', step: 1 })}
            onNext={() => dispatch({ type: 'SET_STEP', step: 3 })}
            onCancel={cancel}
          />
        )}
        {state.step === 3 && (
          <Step3Details
            data={state.data}
            onPatch={(patch) => dispatch({ type: 'PATCH', patch })}
            onBack={() => dispatch({ type: 'SET_STEP', step: 2 })}
            onNext={() => dispatch({ type: 'SET_STEP', step: 4 })}
            onCancel={cancel}
          />
        )}
        {state.step === 4 && (
          <Step4Confirm
            data={state.data}
            isSubmitting={create.isPending}
            onBack={() => dispatch({ type: 'SET_STEP', step: 3 })}
            onSubmit={submit}
            onCancel={cancel}
          />
        )}
      </div>

      <NewClientDialog
        open={newClientOpen}
        onOpenChange={setNewClientOpen}
        onCreated={(client) => {
          dispatch({ type: 'PATCH', patch: { client } });
        }}
      />
    </div>
  );
}
